import { useTranslation } from '@chr/online-shared-ui-localizations';
import { DataGridPro, GridRow, LicenseInfo } from '@mui/x-data-grid-pro';
import { styled, Popper } from '@mui/material';
import { useWindowSize } from '@/common/hooks/useWindowSize';
import { useConfiguration } from '@/common/hooks/useConfiguration';
import { TrackingEventWithId } from '@/common/internalModels';
import { useEffect } from 'react';
import './TransportationLog.scss';
import { columnDefinitions } from './columnDefinitions';

export function TransportationLogTable({rows, isOcean}: {rows: TrackingEventWithId[], isOcean: boolean}){
  const translate = useTranslation();
  const { width: screenWidth } = useWindowSize();
  const config = useConfiguration();

  useEffect(() => {
    if(config){
      LicenseInfo.setLicenseKey(config.muiDataGridLicenseKey);
    }
  }, [config]);

  if(!config){
    return null;
  }

  const columns = columnDefinitions(translate, isOcean);
  
  return (
    <DataGridPro
      columnHeaderHeight={screenWidth >= 862 ? 56 : 72}
      columns={columns}
      rows={rows}
      pagination={true}
      pageSizeOptions={[50, 100, { value: rows.length, label: 'All' }]}
      sortingOrder={['desc', 'asc']}
      sx={gridSx(screenWidth)}
      slots={{
        row: StripedGridRow,
        basePopper: OptionsPopper,
      }}
      slotProps={{
        pagination: {
          sx: paginationSx
        },
      }}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 50, page: 0 },
        },
        sorting: {
          sortModel: [
            {
              field: 'eventDate',
              sort: 'desc',
            },
          ],
        },
      }}
      disableColumnSelector={true}
      disableColumnPinning={true}
      disableColumnReorder={true}
      disableRowSelectionOnClick={true}
      disableMultipleRowSelection={true}
      disableVirtualization={true}
      disableColumnResize={true}
      getRowHeight={() => 'auto'}
      getEstimatedRowHeight={() => 54}
    />
  );
};

export const gridSx = (width: number) => {
  const styling = {
    fontSize: '14px',
    '& .MuiDataGrid-main': { border: '1px solid #E5E6E7' },
    '& .MuiDataGrid-cell': { padding: '8px 16px', lineHeight: '24px', letterSpacing: '.5px' },
    '& .MuiDataGrid-columnHeader': { paddingLeft: '16px' },
    '& .MuiDataGrid-columnHeaders': { borderBottom: '1px solid #aaaeb0', maxHeight: 'unset', height: 'unset' },
    '& .MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus-within': { outline: 'none' },
    '& .MuiSvgIcon-fontSizeMedium': { fontSize: '20px' },
    '& .MuiSvgIcon-fontSizeSmall': { fontSize: '18px', color: '#212121' },
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'normal', whiteSpace: 'normal', maxHeight: 'unset', lineHeight: '24px' },
    '& .MuiDataGrid-columnHeaderTitleContainer': { lineHeight: 'unset', height: 'unset', display: 'flex', alignItems: 'bottom' },
    '& .MuiDataGrid-columnSeparator, .MuiDataGrid-columnSeparator--sideRight': { color: '#c1c1c1' },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': { },
    '& .MuiDataGrid-cellContent': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal' },
    '& .MuiDataGrid-iconSeparator': { display: 'none' },
    '& .MuiIconButton-sizeSmall': { fontSize: '14px', borderRadius: '14px' }
  };

  // window width of 741px is when the grid would be 600px
  // let's hide the scrollbar for the larger screens when we are using flex column widths
  if (width >= 730) {
    styling['& .MuiDataGrid-virtualScroller::-webkit-scrollbar'] =  { display: 'none' };
  }

  return styling;
};

export const StripedGridRow = styled(GridRow)(() =>
  ({
    '&:nth-of-type(odd), &:nth-of-type(odd):hover': { backgroundColor: '#f6f7f8' },
    '&:nth-of-type(even), &:nth-of-type(even):hover': { backgroundColor: '#ffffff' },
  })
);

export const OptionsPopper = styled(Popper)(() =>
  ({
    '& .MuiSvgIcon-fontSizeSmall': { fontSize: '18px' },
    '& .MuiPaper-root': { boxShadow: '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)' },
  })
);

export const paginationSx = {
  '& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '14px' },
  '& .MuiInputBase-root': { fontSize: '14px' },
  '& .MuiTablePagination-selectIcon': { right: '-20px', top: '5px' },
  '& div.MuiTablePagination-select': { paddingBottom: '5px', paddingLeft: '8px', paddingRight: '24px', paddingTop: '4px' },
  '& p': { margin: 0 },
};